import { useState } from 'react';
import { getRecipe } from '../domain/recipes';
import { RECIPE_META } from '../domain/recipeMeta';
import { recipeImage } from '../domain/recipeImages';
import { config } from '../lib/config';

type Ratio = '4/5' | '3/4' | '1/1' | '4/3' | '16/10' | '2/3';

/**
 * A recipe's photo, or (until a verified photo exists) an abstract plate drawn from the dish's
 * real colours. The placeholder is deliberately not photographic, so it can never be mistaken
 * for a picture of a different dish.
 */
export function RecipeImage({ recipeId, ratio = '4/5', className = '', priority = false, rounded = 'rounded-[20px]' }: {
  recipeId: string; ratio?: Ratio; className?: string; priority?: boolean; rounded?: string;
}) {
  const img = recipeImage(recipeId);
  // Photos come from the R2 bucket; if it can't be reached, fall back to the copy on this site.
  const [source, setSource] = useState<'cdn' | 'local' | 'failed'>(config.imageBaseUrl ? 'cdn' : 'local');
  // Shimmer until the photo has arrived, then fade it in (no grey box, no pop).
  const [loaded, setLoaded] = useState(false);
  const failed = source === 'failed';
  const at = (path: string) => (/^https:\/\//.test(path) ? path : source === 'cdn' ? `${config.imageBaseUrl}${path}` : path);
  const title = getRecipe(recipeId)?.title ?? 'Recipe';
  return (
    <div className={`relative overflow-hidden ${rounded} ${img && !failed && !loaded ? 'skeleton' : 'bg-fill'} ${className}`} style={{ aspectRatio: ratio }}>
      {img && !failed ? (
        <img
          src={at(img.src)}
          srcSet={img.src640 ? `${at(img.src640)} 640w, ${at(img.src)} 1080w` : undefined}
          sizes={ratio === '1/1' ? '128px' : '(min-width: 700px) 480px, 100vw'}
          alt={img.alt}
          width={img.width}
          height={img.height}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          // @ts-expect-error fetchpriority is valid HTML, not yet in React 18 types
          fetchpriority={priority ? 'high' : 'auto'}
          ref={(el) => { if (el?.complete && el.naturalWidth) setLoaded(true); }}
          onLoad={() => setLoaded(true)}
          onError={() => setSource((s) => (s === 'cdn' ? 'local' : 'failed'))}
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      ) : (
        <PlatePlaceholder recipeId={recipeId} label={title} />
      )}
    </div>
  );
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Top-down plate with soft colour fields taken from the dish's structured palette. */
export function PlatePlaceholder({ recipeId, label }: { recipeId: string; label: string }) {
  const meta = RECIPE_META[recipeId];
  const [a, b, c] = meta?.visual.palette ?? ['#E8E2D6', '#C9BFAE', '#8C7F6A'];
  const h = hash(recipeId);
  const r = (n: number, span: number) => ((h >> n) & 0xff) / 255 * span - span / 2;
  const id = `p${h.toString(36)}`;
  return (
    <svg viewBox="0 0 400 500" preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full"
      role="img" aria-label={`${label}. Photo not yet available.`}>
      <defs>
        <radialGradient id={`${id}-bg`} cx="30%" cy="20%" r="90%">
          <stop offset="0" style={{ stopColor: 'rgb(var(--plate-bg))' }} />
          <stop offset="1" style={{ stopColor: 'rgb(var(--plate-bg))', stopOpacity: 0.85 }} />
        </radialGradient>
        <filter id={`${id}-blur`} x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="7" /></filter>
        <filter id={`${id}-shadow`} x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0" dy="14" stdDeviation="18" floodColor="#000" floodOpacity="0.16" /></filter>
        <clipPath id={`${id}-clip`}><circle cx="200" cy="250" r="118" /></clipPath>
      </defs>
      <rect width="400" height="500" fill={`url(#${id}-bg)`} />
      <circle cx="200" cy="250" r="158" style={{ fill: 'rgb(var(--plate))' }} filter={`url(#${id}-shadow)`} />
      <circle cx="200" cy="250" r="128" fill="none" stroke="rgb(0 0 0 / 0.05)" strokeWidth="1.5" />
      <g clipPath={`url(#${id}-clip)`} filter={`url(#${id}-blur)`}>
        <circle cx="200" cy="250" r="120" fill={a} />
        <ellipse cx={168 + r(0, 40)} cy={222 + r(8, 40)} rx="70" ry="54" fill={b} opacity="0.9" transform={`rotate(${r(4, 60)} 200 250)`} />
        <ellipse cx={238 + r(16, 40)} cy={282 + r(24, 30)} rx="44" ry="36" fill={c} opacity="0.85" />
        {[0, 1, 2, 3, 4].map((i) => (
          <circle key={i} cx={150 + ((h >> (i * 5)) & 0x7f) * 0.8} cy={190 + ((h >> (i * 3 + 2)) & 0x7f) * 0.9} r={9 + (i % 3) * 4}
            fill={i % 2 ? c : b} opacity="0.75" />
        ))}
      </g>
    </svg>
  );
}
